import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/de';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";
import { Box, Paper, Typography, Stack, IconButton, Grid } from '@mui/material';
import BookingModal from '../components/BookingModal';
import Court from '../components/Court';
import axios from 'axios';
import { API_BASE_URL } from '../App.js';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Europe/Berlin");

const Timetable = () => {
  const [courts, setCourts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs.tz().startOf('day'));
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [players, setPlayers] = useState([]);
  const [profile, setProfile] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [isFree, setIsFree] = useState(false);
  const [court, setCourt] = useState(0);
  const [timeSlot, setTimeSlot] = useState(dayjs.tz().startOf('day'));
  const [booking, setBooking] = useState({});




  // Initial load for things that don't change with date
  useEffect(() => {
    Promise.all([
      axios.get(`${API_BASE_URL}/api/profile`),
      axios.get(`${API_BASE_URL}/api/activePlayers`),
      axios.get(`${API_BASE_URL}/api/events`)
    ]).then(([profileRes, playersRes, eventsRes]) => {
      setProfile(profileRes.data);
      setPlayers(playersRes.data);
      setEvents(eventsRes.data);
    }).catch(err => {
      console.error("Error fetching initial timetable data:", err);
    });
  }, []);

  // Fetch courts whenever date changes
  useEffect(() => {
    const fetchCourtsForDate = async () => {
      try {
        const formattedDate = selectedDate.format('YYYY-MM-DD');
        const [courtRes, bookingsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/courts?date=${formattedDate}`),
          axios.get(`${API_BASE_URL}/api/allBookings?date=${formattedDate}`)
        ]);
        
        setBookings(bookingsRes.data.filter(b => b.status !== 1));
        
        let activeCourts = [];
        if (courtRes.data.length === 0) {
          const allCourtsCheck = await axios.get(`${API_BASE_URL}/api/courts`);
          if (allCourtsCheck.data.length === 0) {
            await axios.post(`${API_BASE_URL}/api/courts/init`);
            const newCourtsRes = await axios.get(`${API_BASE_URL}/api/courts?date=${formattedDate}`);
            activeCourts = newCourtsRes.data.filter(c => c.isActive !== false);
          } else {
            activeCourts = courtRes.data.filter(c => c.isActive !== false);
          }
        } else {
          activeCourts = courtRes.data.filter(c => c.isActive !== false);
        }
        setCourts(activeCourts);
      } catch (err) {
        console.error("Error fetching courts for date:", err);
      }
    };

    fetchCourtsForDate();
  }, [selectedDate]);

  const handleClick = (slot, free, courtNumber, bookingData) => {
    setTimeSlot(slot);
    setIsFree(!free);
    setCourt(courtNumber);
    setBooking(bookingData);
    setShowModal(true);
  };

  const handleSubmit = (value) => {
    if (!value) return setShowModal(false);
    axios.get(`${API_BASE_URL}/api/allBookings?date=${selectedDate.format('YYYY-MM-DD')}`)
      .then((res) => {
        setBookings(res.data.filter(b => b.status !== 1));
        setShowModal(false);
      });
  };


  return (
        <Box>
            <Grid container spacing={2} justifyContent="center">
                {/* Header Section */}
                <Grid item xs={12}>
                    <Paper 
                        elevation={0}
                        sx={{ 
                            p: 2, 
                            mb: 2, 
                            borderRadius: '12px', 
                            bgcolor: 'rgba(255, 255, 255, 0.9)', 
                            backdropFilter: 'blur(10px)',
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2
                        }}
                    >
                        <Box>
                            <Typography variant="h4" color="primary">
                                {selectedDate.format('dddd')}
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary">
                                {selectedDate.format('DD. MMMM YYYY')}
                            </Typography>
                        </Box>

                        <Stack direction="row" alignItems="center" spacing={2} bgcolor="white" p={1} borderRadius={4} boxShadow={1}>
                            <IconButton 
                                onClick={() => setSelectedDate(prev => prev.subtract(1, 'day'))} 
                                size="small"
                                disabled={selectedDate.isSame(dayjs().subtract(3, 'month'), 'day') || selectedDate.isBefore(dayjs().subtract(3, 'month'), 'day')}
                            >
                                <FaAngleLeft />
                            </IconButton>
                            
                            <DatePicker
                                value={selectedDate}
                                onChange={setSelectedDate}
                                slotProps={{ 
                                    textField: { 
                                        size: 'small', 
                                        variant: 'standard',
                                        sx: { minWidth: 150, textAlign: 'center' },
                                        InputProps: { disableUnderline: true }
                                    } 
                                }}
                                format="DD.MM.YYYY"
                                minDate={dayjs().subtract(3, "month")}
                                maxDate={dayjs().add(1, "year")}
                                disableUnderline
                            />

                            <IconButton 
                                onClick={() => setSelectedDate(prev => prev.add(1, 'day'))} 
                                size="small"
                                disabled={selectedDate.isSame(dayjs().add(1, 'year'), 'day') || selectedDate.isAfter(dayjs().add(1, 'year'), 'day')}
                            >
                                <FaAngleRight />
                            </IconButton>
                        </Stack>
                    </Paper>
                </Grid>

                {/* Courts Grid - renders immediately once court list is available */}
                {courts.length === 0 ? (
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <Typography color="text.secondary">Lädt Buchungsliste</Typography>
                    </Grid>
                ) : (
                  courts.map((court) => (
                      <Grid item xs={6} sm={4} md={4} lg={2} key={court._id || court.courtNumber} sx={{ pl: 2, pt: 2 }}>
                          <Court
                              courtNumber={court.courtNumber}
                              bookings={bookings}
                              selectedDate={selectedDate.format('YYYY-MM-DD')}
                              handleClick={handleClick}
                          />
                      </Grid>
                  ))
                )}
            </Grid>

            {showModal &&
                <BookingModal
                    show={showModal}
                    onHide={handleSubmit}
                    free={isFree}
                    slot={timeSlot}
                    court={court}
                    data={booking}
                    players={players}
                    events={events}
                    profile={profile}
                />
            }
        </Box>
    );
};

export default Timetable;
